import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";

// import { ToastContainer, toast } from "react-toastify";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";

// import { ErrorMessage } from "@hookform/error-message";
import FormError from "../FormError";
import "./CareerJourneyFlow.css";
import MultiSelect, { preferredWorkEnvironment } from "../MultiSelect";
import CareerJourneyPage from "./CareerJourneyPage";
import axiosInstance from "../../axios/axiosInstance";
import { workSetupOptions, EducationCertificateCard } from "./utils/utils";
import SkillsEditor from "./SkillsEditor";
import CandidateProfileEditDialog from "../candidate_profile_edit/CandidateProfileEditDialog";
import { normalizeCertifications } from "../candidate_profile_edit/utils/profileFieldNormalization";
import { Pencil } from "lucide-react";

const formatSalaryInput = (value) => {
  if (value === null || value === undefined || value === "") return "";
  let strVal = value.toString();
  if (strVal.includes(".")) {
    const numVal = Number(strVal);
    if (!isNaN(numVal)) {
      strVal = Math.round(numVal).toString();
    }
  }
  const raw = strVal.replace(/\D/g, "");
  if (!raw) return "";
  const num = Number(raw);
  if (num <= 9999) return raw;
  return raw.replace(/\B(?=(\d{3})+(?!\d))/g, "’");
};

const experienceLevels = [
  { value: "Entry-level", label: "Entry-level" },
  { value: "Professional", label: "Professional" },
  { value: "Expert", label: "Expert" },
  { value: "Leadership", label: "Leadership" },
];

const fluencyLevels = [
  "Basic",
  "Intermediate",
  "Business fluent",
  "Bilingual/Native",
];
const systemLevels = ["Beginner", "Intermediate", "Advanced", "Expert"];

const languageOptions = [
  "English",
  "French",
  "German",
  "Italian",
  "Spanish",
  "Dutch",
  "Portuguese",
  "Polish",
  "Czech",
  "Danish",
  "Swedish",
  "Finnish",
  "Norwegian",
  "Icelandic",
  "Romanian",
  "Hungarian",
  "Greek",
  "Slovak",
  "Slovenian",
  "Croatian",
  "Bulgarian",
  "Estonian",
  "Latvian",
  "Lithuanian",
  "Maltese",
  "Irish",
  "Romansh",
];

function CareerJourneyFlow() {
  const { userId: selectedUserCareerJourneyId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [careerJourneyExists, setCareerJourneyExists] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [readOnlyData, setReadOnlyData] = useState({});
  const [preFillData, setPreFillData] = useState({});
  const [buttonLoading, setButtonLoading] = useState(false);
  const [commonDataExists, setCommonDataExists] = useState(false);
  const [peerReviewStatus, setPeerReviewStatus] = useState(0);

  const [countrySearch, setCountrySearch] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [localCountries, setLocalCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [countries, setCountries] = useState([]);
  const [isCountryLocked, setIsCountryLocked] = useState(false);

  // Skills Editor State
  const [isSkillsEditorOpen, setIsSkillsEditorOpen] = useState(false);
  const [isProfileEditOpen, setIsProfileEditOpen] = useState(false);
  const [skillsEditorData, setSkillsEditorData] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);

  // Store available roles for payload
  const [availableRoles, setAvailableRoles] = useState([]);

  const [tempLanguage, setTempLanguage] = useState("");
  const [tempFluency, setTempFluency] = useState("Intermediate");

  const cap = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "");

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    watch,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      ep: "",
      overview: "",
      mainAchievements: "",
      professional: "",
      personal: "",
      why: "",
      preferredEnvironment: [],
      preferredWorkEnvironment: [],
      educations: [],
      certifications: [],

      developmentPotential: "",
      languages: [],
      noticePeriod: { value: 0, unit: "days" },
      peerReview: 0,
      experienceLevel: "",
      workSetup: [],
      // Fields moved from role to common
      overallExperience: { value: "", unit: "" },
      relevantExperience: [],
      salaryExpectation: "",
      salaryCurrency: "CHF",
      salaryPeriod: "year",
      location: [], // now an array of { country, countryCode, cities: [] }
      newLocation: { country: "", countryCode: "", cityInput: "", cities: [] },

      additionalSystems: [],
      skills: [],
    },
  });
  const handleClose = () => {
    setPeerReviewStatus(0);
    navigate("/candidate-schedules");
  };

  const handleCancel = () => {
    if (careerJourneyExists) {
      setIsEditMode(false);
      // Optionally reset form if needed, but switching to view might be enough
    } else {
      handleClose();
    }
  };

  const [newExperience, setNewExperience] = useState({
    company: "",
    role: "",
    durationValue: "",
    durationUnit: "",
  });

  const [newSystem, setNewSystem] = useState({ system: "", level: "" });

  const [experienceErrors, setExperienceErrors] = useState({});
  const [systemErrors, setSystemErrors] = useState({});

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      // Only search if user typed something *different* from the selected country
      if (
        countrySearch.trim().length > 0 &&
        countrySearch !== watch("newLocation.country")
      ) {
        fetchCountriesSearch(countrySearch);
      } else if (countrySearch.trim().length === 0) {
        setLocalCountries([]);
      }
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [countrySearch]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (
        citySearch.trim().length > 0 &&
        watch("location.countryCode") &&
        citySearch !== watch("location.city")
      ) {
        fetchCities(watch("location.countryCode"), citySearch);
      } else if (citySearch.trim().length === 0) {
        setCities([]);
      }
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [citySearch]);

  // Fetch default country (Switzerland) on mount
  useEffect(() => {
    const fetchDefaultCountry = async () => {
      try {
        const res = await axiosInstance.get(
          `/api/profile/countries?search=Switzerland`,
        );
        const countries = res.data || [];

        const switzerland = countries.find(
          (c) => c.name.toLowerCase() === "switzerland",
        );

        if (switzerland) {
          setValue("newLocation.country", switzerland.name);
          setValue("newLocation.countryCode", switzerland.code);
          setCountrySearch(switzerland.name);
          setIsCountryLocked(true);
        }
      } catch (err) {
        console.error("Failed to fetch default country", err);
      }
    };

    if (selectedUserCareerJourneyId) {
      fetchDefaultCountry();
    }
  }, [selectedUserCareerJourneyId, setValue]);

  const fetchCountriesSearch = async (search) => {
    try {
      setLoadingCountries(true);
      const res = await axiosInstance.get(
        `/api/profile/countries?search=${search}`,
      );
      setLocalCountries(res.data || []);
    } catch (err) {
      console.error("Failed to fetch countries", err);
    } finally {
      setLoadingCountries(false);
    }
  };

  const fetchCities = async (countryCode, name) => {
    try {
      setLoadingCities(true);
      const res = await axiosInstance.get(
        `/api/profile/cities?country_code=${countryCode}&name=${name}`,
      );
      setCities(res.data || []);
    } catch (err) {
      console.error("Failed to fetch cities", err);
    } finally {
      setLoadingCities(false);
    }
  };

  /** ---------------- FETCH COMMON DATA ---------------- **/
  const fetchCareerJourneyCommonData = async () => {
    try {
      const res = await axiosInstance.get(
        `/api/admin/career-journey/${selectedUserCareerJourneyId}`,
      );
      const data = res.data;
      const _data = {
        ep:
          data?.common?.ep && data?.common?.ep !== ""
            ? data?.common?.ep
            : preFillData?.ep || "",
        overview: data?.common?.overview || "",
        mainAchievements: data?.common?.mainAchievements || "",
        professional: data?.common?.professional || "",
        personal: data?.common?.personal || "",
        why: data?.common?.why || "",
        preferredEnvironment: data?.common?.preferredEnvironment || [],
        preferredWorkEnvironment: data?.common?.preferredWorkEnvironment || [],

        developmentPotential: data?.common?.developmentPotential || "",
        languages:
          data?.common?.languages.length > 0
            ? data.common?.languages
            : preFillData?.languages || [],
        noticePeriod: data?.noticePeriod || {},
        peerReview: data?.common?.peerReview || 0,
        educationList: data?.common?.education || preFillData?.educations || [],
        certificationList:
          data?.common?.certifications || preFillData?.certifications || [],
        workSetup:
          data?.common?.workSetup.length > 0
            ? data.common?.workSetup
            : watch("workSetup") || [],
        experienceLevel: data?.common?.experienceLevel || "",
        // Fields moved from role to common
        overallExperience:
          typeof data?.common?.overallExperience === "object" &&
            data?.common?.overallExperience !== null
            ? data.common.overallExperience
            : typeof data?.common?.overallExperience === "string" &&
              data?.common?.overallExperience
              ? { value: data.common.overallExperience, unit: "" }
              : { value: "", unit: "" },
        relevantExperience: data?.common?.relevantExperience || [],
        noticePeriodDays: data?.common?.noticePeriodDays || "",
        salaryExpectation: data?.common?.salaryExpectation || "",
        salaryCurrency: data?.common?.salaryCurrency || "CHF",
        salaryPeriod: data?.common?.salaryPeriod || "year",
        location: data?.common?.location || [],

        additionalSystems: data?.common?.additionalSystems || [],
        skills: data?.common?.skills || [],
      };
      reset((prev) => ({ ...prev, ..._data }));
      setCommonDataExists(true);
      // Store peerReview status from API - handle number, string, or boolean
      const peerReviewValue = data?.common?.peerReview;
      const peerReviewStatusValue =
        peerReviewValue === 1 ||
          peerReviewValue === "1" ||
          peerReviewValue === true
          ? 1
          : 0;
      setPeerReviewStatus(peerReviewStatusValue);
    } catch (error) {
      console.error(error);
    }
  };

  /** ---------------- FETCH PREFILL DATA ---------------- **/
  const fetchPreFillData = async () => {
    try {
      const res = await axiosInstance.get(
        `/api/admin/career-journey/${selectedUserCareerJourneyId}/pre-fill/`,
      );
      const data = res.data;
      setCareerJourneyExists(res.data.cjAlreadyExists);
      setPreFillData({
        ep: data.ep || "",
        languages: data.formattedLanguages || [],
        noticePeriod: data?.noticePeriod || {},
        rolesData: data?.preferredRoles || [],
        workSetup: data?.workSetup || [],
        skills: data?.skills || [],
      });

      // console.log("Prefill data:", data);
      // Store available roles for payload
      setAvailableRoles(data.preferredRoles || []);

      reset((prev) => ({
        ...prev,
        ep: data.ep || "",
        languages: data.formattedLanguages || [],
        noticePeriod: data?.noticePeriod || {},
        workSetup: data?.workSetup,
        educations: data?.educations || [],
        certifications: normalizeCertifications(data?.certifications || []),
        skills: data?.skills || [],
      }));
    } catch (err) {
      console.error("Error fetching prefill:", err);
    }
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  // Reset peerReviewStatus only when user changes
  useEffect(() => {
    setPeerReviewStatus(0);
  }, [selectedUserCareerJourneyId]);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true); // Ensure loading is true at start of effect
      if (selectedUserCareerJourneyId) {
        try {
          await fetchPreFillData();
          await fetchCareerJourneyCommonData();
        } catch (error) {
          toast.error("Failed to fetch career journey.");
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false); // If no ID, stop loading
      }
    };

    fetchAllData();
  }, [selectedUserCareerJourneyId, careerJourneyExists]);

  useEffect(() => {
    if (careerJourneyExists) {
      fetchCareerJourneyFullData();
    }
  }, [careerJourneyExists]);

  const fetchCareerJourneyFullData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/api/admin/career-journey/full/${selectedUserCareerJourneyId}`,
      );
      setReadOnlyData(res.data);
      setLoading(false);
    } catch (error) {
      console.log("failed to get the data", error);
      setLoading(false);
    }
  };

  /** ---------------- HANDLE FORM SUBMIT ---------------- **/
  const handleSubmitForm = async (data) => {
    setButtonLoading(true);
    try {
      // Prepare common data payload
      const commonPayload = {
        overview: data.overview,
        ep: data.ep,
        mainAchievements: data.mainAchievements,
        professional: data.professional,
        personal: data.personal,
        why: data.why,
        preferredEnvironment: data.preferredEnvironment || [],
        preferredWorkEnvironment: data.preferredWorkEnvironment || [],

        developmentPotential: data.developmentPotential,
        languages: data.languages || [],
        educations: data.educations || [],
        certifications: normalizeCertifications(data.certifications || []),
        peerReview: data.peerReview || 0,
        employmentType: data.employmentType || [],
        willRelocate: data.willRelocate || null,
        workSetup: data.workSetup || [],
        experienceLevel: data.experienceLevel || "",
        // Fields moved from role to common
        overallExperience:
          data.overallExperience &&
            (data.overallExperience.value || data.overallExperience.unit)
            ? data.overallExperience
            : null,
        relevantExperience: data.relevantExperience || [],
        salaryExpectation: data.salaryExpectation || null,
        salaryCurrency: data.salaryCurrency || null,
        salaryPeriod: data.salaryPeriod || "year",
        location: data.location || [],
        noticePeriod: data.noticePeriod || { value: 0, unit: "days" },
        additionalSystems: data.additionalSystems || [],
      };

      // Include id in common payload if updating
      if (commonDataExists && readOnlyData?.common?.id) {
        commonPayload.id = readOnlyData.common.id;
        commonPayload.userId = selectedUserCareerJourneyId;
      }

      // Prepare roles array (just role IDs, no role-specific data)
      const rolesPayload = availableRoles.map((role) => ({
        userJobRoleId: role.id,
        roleTitle: role.RoleName,
      }));

      // Final payload structure
      const payload = {
        userId: selectedUserCareerJourneyId,
        ...commonPayload,
      };

      let response;
      if (commonDataExists) {
        response = await axiosInstance.put(
          `/api/admin/career-journey/common/${selectedUserCareerJourneyId}`,
          payload,
        );
      } else {
        response = await axiosInstance.post(
          `/api/admin/career-journey/common`,
          payload,
        );
      }

      if (response?.data) {
        setIsEditMode(false);
        handleClose();
        toast.success("Career journey saved successfully!");
        // Refresh the data
        await fetchCareerJourneyFullData();
      }
    } catch (err) {
      console.error("Error saving:", err);
      toast.error("Failed to save career journey.");
    } finally {
      setButtonLoading(false);
    }
  };
  /** ---------------- SKILLS EDITOR ---------------- **/
  const handleEditSkills = async () => {
    setLoadingSkills(true);
    try {
      // 1. Identify User Roles
      // We need jobFunctionId and jobRoleId for each role the user has.
      // The `availableRoles` state holds the roles selected in the pre-fill or common data.
      // However, `availableRoles` might just have IDs. Let's check `preFillData` or `readOnlyData`.
      // The `skills` in `watch("skills")` has `user_job_role_id`, `job_function_id`, `job_role_id`.

      const currentSkills = watch("skills") || [];

      // If no skills data yet, we might need to rely on `availableRoles` or `preFillData.preferredRoles`.
      // Let's assume `availableRoles` has the necessary info or we can get it from `currentSkills` if it was populated from backend.
      // If `currentSkills` is empty, we might not be able to fetch specific skills unless we know the roles.
      // But `fetchPreFillData` sets `availableRoles`.

      // Let's iterate over `availableRoles` (which should be the source of truth for selected roles).
      // If `availableRoles` is empty, check `currentSkills`.

      let rolesToProcess = [];

      if (availableRoles.length > 0) {
        rolesToProcess = availableRoles.map((r) => ({
          jobFunctionId: r.function || r.JobFunctionId || r.job_function_id,
          jobRoleId: r.role || r.job_role_id, // Prioritize r.role as it seems to be the job_role_id
          roleName: r.RoleName || r.job_role_name,
          userJobRoleId: r.id || r.userJobRoleId, // r.id appears to be user_job_role_id
        }));
      } else if (currentSkills.length > 0) {
        rolesToProcess = currentSkills.map((s) => ({
          jobFunctionId: s.job_function_id,
          jobRoleId: s.job_role_id,
          roleName: s.job_role_name,
          userJobRoleId: s.user_job_role_id,
        }));
      }

      if (rolesToProcess.length === 0) {
        console.warn("No roles found to fetch skills for.");
        toast.warn("No roles found to fetch skills for.");
        setLoadingSkills(false);
        return;
      }

      const editorData = [];

      for (const role of rolesToProcess) {
        // Fetch available skills for this role
        // Endpoint: /api/profile/job-role-skills/:jobFunctionId/:jobRoleId
        // Note: User said "http://localhost:3500/api/profile/job-role-skills/1/1"

        if (!role.jobFunctionId || !role.jobRoleId) {
          console.warn("Missing jobFunctionId or jobRoleId for role:", role);
          continue;
        }

        const res = await axiosInstance.get(
          `/api/profile/job-role-skills/${role.jobFunctionId}/${role.jobRoleId}`,
        );
        const data = res.data;

        // Process available skills
        // The API returns must_have, should_have, could_have arrays.
        // specific_skills are mapped by index.

        const allSkillsMap = new Map();

        const processCategory = (skillsArr, specificSkillsArr) => {
          if (!skillsArr || !specificSkillsArr) return;
          skillsArr.forEach((skillName, index) => {
            if (!allSkillsMap.has(skillName)) {
              allSkillsMap.set(skillName, {
                name: skillName,
                subSkills: new Set(),
              });
            }
            const skillEntry = allSkillsMap.get(skillName);
            const subSkills = specificSkillsArr[index];
            if (Array.isArray(subSkills)) {
              subSkills.forEach((sub) => skillEntry.subSkills.add(sub));
            }
          });
        };

        processCategory(data.must_have_skills, data.must_have_specific_skills);
        processCategory(
          data.should_have_skills,
          data.should_have_specific_skills,
        );
        processCategory(
          data.could_have_skills,
          data.could_have_specific_skills,
        );

        const currentRoleSkills = currentSkills.find(
          (s) => s.job_role_id === role.jobRoleId,
        ) || { skills: [] };

        const roleSkillsForEditor = Array.from(allSkillsMap.values()).map(
          (skillEntry) => {
            const skillName = skillEntry.name;
            const allSubSkills = Array.from(skillEntry.subSkills);

            const selectedSkillObj = currentRoleSkills.skills?.find(
              (s) => Object.keys(s)[0] === skillName,
            );
            const selectedSkillData = selectedSkillObj
              ? selectedSkillObj[skillName]
              : null;

            // Normalize sub-skills to objects if they are strings
            const normalizedSubSkills = (
              selectedSkillData?.subSkills || []
            ).map((sub) => {
              if (typeof sub === "string") {
                return { name: sub, level: "Intermediate" };
              }
              return sub;
            });

            return {
              name: skillName,
              level: selectedSkillData?.level || "Intermediate",
              subSkills: normalizedSubSkills,
              allSubSkills: allSubSkills,
              isSelected: !!selectedSkillObj,
            };
          },
        );

        editorData.push({
          roleId: role.jobRoleId,
          roleName: role.roleName,
          jobFunctionId: role.jobFunctionId,
          userJobRoleId: role.userJobRoleId,
          skills: roleSkillsForEditor,
        });
      }

      setSkillsEditorData(editorData);
      setIsSkillsEditorOpen(true);
    } catch (err) {
      console.error("Failed to fetch skills", err);
      toast.error("Failed to load skills data.");
    } finally {
      setLoadingSkills(false);
    }
  };

  const handleSaveSkills = (updatedRolesData) => {
    // Construct payload for API/Controller
    const payload = updatedRolesData.map((role) => {
      const selectedSkills = role.skills
        .filter((s) => s.isSelected)
        .map((s) => ({
          [s.name]: {
            level: s.level
              ? s.level.charAt(0).toUpperCase() + s.level.slice(1).toLowerCase()
              : "Intermediate", // Ensure Title Case
            subSkills: (s.subSkills || []).map((sub) => ({
              name: sub.name,
              level: sub.level
                ? sub.level.charAt(0).toUpperCase() +
                sub.level.slice(1).toLowerCase()
                : "Intermediate", // Ensure Title Case for sub-skills
            })),
          },
        }));

      // Try to find extra role details if available in availableRoles
      const originalRole =
        availableRoles.find((r) => r.id === role.userJobRoleId) || {};

      return {
        id: role.userJobRoleId,
        function: role.jobFunctionId,
        role: role.roleId,
        employmentType: originalRole.employmentType || "Full-time", // Default or fetch if available
        willRelocate: originalRole.willRelocate || "No",
        location_type: originalRole.location_type || "On-site",
        workSetup: originalRole.workSetup || [],
        locationType: originalRole.locationType || "On-site",
        skills: selectedSkills,
      };
    });

    // Update Form State for UI Rendering
    // We keep the structure compatible with the current JSX rendering
    const updatedFormSkills = updatedRolesData.map((role) => {
      const selectedSkills = role.skills
        .filter((s) => s.isSelected)
        .map((s) => ({
          [s.name]: {
            level: s.level,
            subSkills: (s.subSkills || []).map((sub) => ({
              name: sub.name,
              level: sub.level,
            })),
          },
        }));

      return {
        user_job_role_id: role.userJobRoleId,
        job_function_id: role.jobFunctionId,
        job_role_id: role.roleId,
        job_role_name: role.roleName,
        skills: selectedSkills,
      };
    });

    // Call API to save skills
    axiosInstance
      .put(
        `/api/admin/candidates/${selectedUserCareerJourneyId}/skills`,
        payload,
      )
      .then((res) => {
        if (res.data.success) {
          toast.success(
            res.data.message || "Candidate skills updated successfully",
          );
          setValue("skills", updatedFormSkills, { shouldValidate: true });
          setIsSkillsEditorOpen(false);
        } else {
          toast.error(res.data.message || "Failed to update skills");
          // Keep dialog open and update the editor data so user doesn't lose changes
          setSkillsEditorData(updatedRolesData);
        }
      })
      .catch((err) => {
        console.error("Failed to update skills", err);
        const errorMsg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to update skills";
        toast.error(errorMsg);
        // Keep dialog open and update the editor data so user doesn't lose changes
        setSkillsEditorData(updatedRolesData);
      });
  };
  const handleAddExperience = () => {
    // Reset errors
    setExperienceErrors({});

    let currentErrors = {};
    let hasError = false;

    const { company, role, durationValue, durationUnit } = newExperience;

    // 1. Company and Role required
    if (!company.trim()) {
      currentErrors.company = "Company is required";
      hasError = true;
    }
    if (!role.trim()) {
      currentErrors.role = "Role is required";
      hasError = true;
    }

    // 2. Duration and Unit required
    if (!durationValue) {
      currentErrors.durationValue = "Duration Value is required";
      hasError = true;
    }
    if (!durationUnit) {
      currentErrors.durationUnit = "Unit is required";
      hasError = true;
    }

    if (hasError) {
      setExperienceErrors(currentErrors);
      // toast.warn("Please check the fields for errors."); // Optional, relying on inline
      return;
    }

    const durationStr =
      durationValue && durationUnit
        ? `${durationValue} ${durationUnit}`
        : "N/A";

    const experienceToAdd = {
      company: company,
      role: role,
      duration: durationStr,
    };

    const currentExperiences = watch("relevantExperience") || [];
    setValue("relevantExperience", [...currentExperiences, experienceToAdd], {
      shouldValidate: true,
    });

    setNewExperience({
      company: "",
      role: "",
      durationValue: "",
      durationUnit: "",
    });
    setExperienceErrors({});
  };

  const handleRemoveExperience = (index) => {
    const currentExperiences = watch("relevantExperience") || [];
    const updated = currentExperiences.filter((_, i) => i !== index);
    setValue("relevantExperience", updated, { shouldValidate: true });
  };

  const handleAddSystem = () => {
    setSystemErrors({});
    let currentErrors = {};
    let hasError = false;

    const { system, level } = newSystem;

    if (system.trim() && !level.trim()) {
      currentErrors.level = "Level is required";
      hasError = true;
    }
    if (level.trim() && !system.trim()) {
      currentErrors.system = "System is required";
      hasError = true;
    }
    if (!system.trim() && !level.trim()) {
      currentErrors.system = "System is required";
      currentErrors.level = "Level is required";
      hasError = true;
    }

    if (hasError) {
      setSystemErrors(currentErrors);
      return;
    }

    const currentSystems = watch("additionalSystems") || [];
    setValue("additionalSystems", [...currentSystems, newSystem], {
      shouldValidate: true,
    });
    setNewSystem({ system: "", level: "" });
    setSystemErrors({});
  };

  const handleRemoveSystem = (index) => {
    const currentSystems = watch("additionalSystems") || [];
    const updated = currentSystems.filter((_, i) => i !== index);
    setValue("additionalSystems", updated, { shouldValidate: true });
  };

  // if (!isOpen) return null;

  if (careerJourneyExists && !isEditMode && readOnlyData) {
    return (
      <>
        <CareerJourneyPage
          readOnlyData={readOnlyData}
          handleEditClick={handleEditClick}
          onEditProfileClick={() => setIsProfileEditOpen(true)}
          isClose={handleClose}
          preFillData={preFillData}
        />
        <CandidateProfileEditDialog
          isOpen={isProfileEditOpen}
          onClose={() => setIsProfileEditOpen(false)}
          userId={selectedUserCareerJourneyId}
          onSaved={fetchPreFillData}
        />
      </>
    );
  }
  return (
    <>
      <div className="main-content career-journey-container">
        <div className="container">
          <div
            className="page-header"
            style={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "1.5rem",
              marginTop: "1rem",
            }}
          >
            <Link
              to="/candidate-schedules"
              className="back-link"
              style={{
                position: "absolute",
                left: 0,
                margin: 0,
              }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
              Back to Schedules
            </Link>
            <h1
              className="page-title"
              style={{
                margin: 0,
                textAlign: "center",
              }}
            >
              Career Journey
            </h1>

            <div
              className="cj-header-actions"
              style={{
                position: "absolute",
                right: 0,
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
              }}
            >
              <button
                type="button"
                className="cj-edit-profile-btn"
                onClick={() => setIsProfileEditOpen(true)}
              >
                <Pencil size={18} /> Edit Profile
              </button>
              {(peerReviewStatus === 1 ||
                peerReviewStatus === "1" ||
                peerReviewStatus === true) && (
                  <div
                    className="peer-review-badge-inline"
                    title="This profile has been reviewed by one or more hiring managers who assessed the candidate’s experience, skills, and professional credibility."
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="20"
                      width="20"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M23 12l-2.44-2.79.34-3.7L17.3 4l-1.99-3-3.31 1.89L9.02 1 7.03 4l-3.6.51.34 3.7L1 12l2.77 2.53-.34 3.7 3.6.51L9.02 23l3-1.89L15.31 23l1.99-3 3.6-.51-.34-3.7zM10.09 16.36l-3.5-3.58 1.41-1.41 2.09 2.14 5.91-6L17.41 8z" />
                    </svg>
                    <span>Hiring Manager Evaluated</span>
                  </div>
                )}
            </div>
          </div>

          {loading ? (
            <div className="loader-container">
              <div className="spinner"></div>
              <p>Loading career journey data...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit(handleSubmitForm)}>
              <div className="form-content-wrapper">
                <div className="cj-section">
                  <h3 className="section-title">Candidate Information</h3>

                  <div className="form-spacing">
                    <label>Candidate Name</label>
                    <input
                      {...register("ep")}
                      value={watch("ep") || preFillData?.ep || ""}
                      disabled
                    />
                  </div>

                  <div className="form-grid-2">
                    <div>
                      <label>Overview *</label>
                      <textarea
                        {...register("overview", {
                          required: "Overview is required",
                        })}
                      />
                      <FormError message={errors.overview?.message} />

                      {/* <ErrorMessage
                        errors={errors}
                        name="overview"
                        render={({ message }) => (
                          <p
                            style={{
                              color: "red",
                              fontSize: "14px",
                              marginTop: "5px",
                            }}
                          >
                            {message}
                          </p>
                        )}
                      /> */}
                    </div>
                    <div>
                      <label>Main Achievements *</label>
                      <textarea
                        {...register("mainAchievements", {
                          required: "Main Achievements is required",
                        })}
                      />
                      <FormError message={errors.mainAchievements?.message} />

                      {/* <ErrorMessage
                        errors={errors}
                        name="mainAchievements"
                        render={({ message }) => (
                          <p
                            style={{
                              color: "red",
                              fontSize: "14px",
                              marginTop: "5px",
                            }}
                          >
                            {message}
                          </p>
                        )}
                      /> */}
                    </div>
                  </div>

                  <div className="form-grid-2">
                    <div>
                      <label>Professional value add *</label>
                      <textarea
                        {...register("professional", {
                          required: "Professional Value is required",
                        })}
                      />
                      <FormError message={errors.professional?.message} />
                    </div>
                    <div>
                      <label>Personal value add *</label>
                      <textarea
                        {...register("personal", {
                          required: "Personal Value is required",
                        })}
                      />
                      <FormError message={errors.personal?.message} />
                    </div>
                  </div>

                  <div className="form-grid-2">
                    <div>
                      <label>Experience Level *</label>
                      <select
                        {...register("experienceLevel", {
                          required: "Experience Level is required",
                        })}
                        defaultValue=""
                      >
                        <option value="" disabled>
                          Select experience level
                        </option>
                        {experienceLevels.map((level) => (
                          <option key={level.value} value={level.value}>
                            {level.label}
                          </option>
                        ))}
                      </select>
                      <FormError message={errors.experienceLevel?.message} />
                    </div>

                    <div className="form-field-full">
                      <label>Notice Period *</label>
                      <div className="notice-period-grid">
                        <div>
                          <input
                            type="number"
                            min="0"
                            placeholder="Value"
                            {...register("noticePeriod.value", {
                              valueAsNumber: true,
                              required: "Notice period value is required",
                              validate: (val) => {
                                const unit = getValues("noticePeriod.unit");
                                if (val > 0 && !unit) {
                                  return "Unit is required";
                                }
                                if (
                                  unit &&
                                  (val === undefined || val === null || val < 0)
                                ) {
                                  return "Value cannot be negative";
                                }
                                return true;
                              },
                            })}
                            defaultValue={
                              watch("noticePeriod.value") ||
                              preFillData?.noticePeriod?.value ||
                              0
                            }
                          />
                          <FormError
                            message={errors.noticePeriod?.value?.message}
                          />
                        </div>
                        <div>
                          <select
                            defaultValue={
                              watch("noticePeriod.unit") ||
                              preFillData?.noticePeriod?.unit ||
                              ""
                            }
                            {...register("noticePeriod.unit", {
                              required: "Unit is required",
                              validate: (val) => {
                                const value = getValues("noticePeriod.value");
                                if (value > 0 && !val) {
                                  return "Unit is required";
                                }
                                return true;
                              },
                            })}
                          >
                            <option value="">Select unit</option>
                            <option value="days">Days</option>
                            <option value="weeks">Weeks</option>
                            <option value="months">Months</option>
                          </select>
                          <FormError
                            message={errors.noticePeriod?.unit?.message}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-spacing">
                    <label>The WHY (purpose and drivers) *</label>
                    <textarea
                      {...register("why", {
                        required: "The WHY is required",
                      })}
                    />
                    <FormError message={errors.why?.message} />
                  </div>

                  <div className="form-grid-2">
                    <div>
                      <label>Preferred Company Type *</label>
                      <Controller
                        control={control}
                        name="preferredEnvironment"
                        rules={{
                          required: "Preferred Company Type is required",
                          validate: (val) =>
                            (val && val.length > 0) ||
                            "Please select at least one environment",
                        }}
                        render={({ field }) => (
                          <MultiSelect
                            value={field.value || []}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      {/* {errors.preferredEnvironment && (
                        <p
                          style={{
                            color: "red",
                            fontSize: "14px",
                            marginTop: "5px",
                          }}
                        >
                          {errors.preferredEnvironment.message}
                        </p>
                      )} */}
                      <FormError
                        message={errors.preferredEnvironment?.message}
                      />
                    </div>

                    <div>
                      <label>Preferred Work Environment *</label>
                      <Controller
                        control={control}
                        name="preferredWorkEnvironment"
                        rules={{
                          required: "Preferred Work Environment is required",
                          validate: (val) =>
                            (val && val.length > 0) ||
                            "Please select at least one environment",
                        }}
                        render={({ field }) => (
                          <MultiSelect
                            value={field.value || []}
                            onChange={field.onChange}
                            options={preferredWorkEnvironment}
                          />
                        )}
                      />
                      {/* {errors.preferredWorkEnvironment && (
                        <p
                          style={{
                            color: "red",
                            fontSize: "14px",
                            marginTop: "5px",
                          }}
                        >
                          {errors.preferredWorkEnvironment.message}
                        </p>
                      )} */}
                      <FormError
                        message={errors.preferredWorkEnvironment?.message}
                      />
                    </div>
                  </div>

                  <div className="form-grid-2">
                    <div>
                      <label>Work Setup *</label>
                      <Controller
                        control={control}
                        name="workSetup"
                        rules={{
                          required: "Work Setup is required",
                          validate: (val) =>
                            (val && val.length > 0) ||
                            "Please select at least one work setup",
                        }}
                        render={({ field }) => (
                          <MultiSelect
                            value={field.value || []}
                            onChange={field.onChange}
                            options={workSetupOptions.map((opt) => ({
                              label: opt.name,
                              value: opt.id,
                            }))}
                          />
                        )}
                      />
                      <FormError message={errors.workSetup?.message} />
                    </div>
                    <div>
                      {/* area of improvements is same as development potential */}
                      <label>Areas of Improvements *</label>
                      <input
                        type="text"
                        {...register("developmentPotential", {
                          required: "Areas of Improvements is required",
                        })}
                      />

                      {/* <ErrorMessage
                        errors={errors}
                        name="developmentPotential"
                        render={({ message }) => (
                          <p
                            style={{
                              color: "red",
                              fontSize: "14px",
                              marginTop: "5px",
                            }}
                          >
                            {message}
                          </p>
                        )}
                      /> */}
                      <FormError
                        message={errors.developmentPotential?.message}
                      />
                    </div>
                  </div>

                  <div className="new-language-ui">
                    <input
                      type="hidden"
                      {...register("languages", {
                        validate: (val) =>
                          (val && val.length > 0) ||
                          "At least one language is required",
                      })}
                    />
                    <div className="form-grid-2">
                      <div>
                        <div className="system-field">
                          <label>Select Language *</label>
                          <select
                            value={tempLanguage}
                            onChange={(e) => setTempLanguage(e.target.value)}
                          >
                            <option value="">Select Language</option>
                            {languageOptions.sort().map((l) => (
                              <option key={l} value={l}>
                                {l}
                              </option>
                            ))}
                          </select>
                        </div>
                        <FormError message={errors.languages?.message} />
                      </div>

                      <div>
                        <div className="fluency-add-row">
                          <div className="system-field" style={{ flex: 1 }}>
                            <label>Fluency *</label>
                            <select
                              value={tempFluency}
                              onChange={(e) => setTempFluency(e.target.value)}
                            >
                              {fluencyLevels.map((f) => (
                                <option key={f} value={f}>
                                  {f}
                                </option>
                              ))}
                            </select>
                          </div>
                          <button
                            type="button"
                            className="add-lang-btn"
                            onClick={() => {
                              const current = watch("languages") || [];
                              if (!tempLanguage) {
                                setError("languages", {
                                  type: "manual",
                                  message: "Please select a language",
                                });
                                return;
                              }
                              if (
                                current.some((l) => l.name === tempLanguage)
                              ) {
                                setError("languages", {
                                  type: "manual",
                                  message: "Language already added",
                                });
                                return;
                              }
                              clearErrors("languages");
                              setValue(
                                "languages",
                                [
                                  ...current,
                                  { name: tempLanguage, fluency: tempFluency },
                                ],
                                { shouldValidate: true },
                              );
                              setTempLanguage("");
                              setTempFluency("Intermediate");
                            }}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* <ErrorMessage
                      errors={errors}
                      name="languages"
                      render={({ message }) => (
                        <p
                          style={{
                            color: "red",
                            fontSize: "14px",
                            marginTop: "5px",
                          }}
                        >
                          {message}
                        </p>
                      )}
                    /> */}

                    <div className="language-tags-container">
                      {(watch("languages") || []).map((lang, i) => (
                        <span key={i} className="language-tag">
                          {lang.name} ({lang.fluency})
                          <span
                            className="remove-tag"
                            onClick={() => {
                              const current = watch("languages");
                              setValue(
                                "languages",
                                current.filter((_, idx) => idx !== i),
                              );
                            }}
                          >
                            ×
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="cj-section-inner">
                  <EducationCertificateCard
                    title="Education"
                    items={watch("educations") || preFillData?.educations || []}
                    type="education"
                  />
                </div>

                <div className="cj-section-inner">
                  <EducationCertificateCard
                    title="Certifications"
                    items={
                      watch("certifications") ||
                      preFillData?.certifications ||
                      []
                    }
                    type="certification"
                  />
                </div>

                <div className="cj-section">
                  <div
                    className="section-header"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "5px",
                      marginBottom: "10px",
                    }}
                  >
                    <h3 className="section-title">Skills *</h3>
                    <button
                      type="button"
                      className="edit-skills-btn"
                      onClick={handleEditSkills}
                      title="Edit Skills"
                    >
                      {loadingSkills ? (
                        <span
                          className="button-spinner"
                          style={{
                            width: "12px",
                            height: "12px",
                            borderTopColor: "#64748b",
                            borderRightColor: "#64748b",
                            borderBottomColor: "#64748b",
                          }}
                        ></span>
                      ) : (
                        <>
                          <span style={{ marginRight: "4px" }}>Edit</span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                          >
                            <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z" />
                          </svg>
                        </>
                      )}
                    </button>
                  </div>
                  <input
                    type="hidden"
                    {...register("skills", {
                      validate: (val) =>
                        (val && val.length > 0) ||
                        "At least one skill is required",
                    })}
                  />
                  <div style={{ marginBottom: "10px" }}>
                    <FormError message={errors.skills?.message} />
                  </div>
                  {watch("skills") &&
                    watch("skills").map((roleSkill, index) => (
                      <div
                        key={index}
                        className="skill-role-group"
                        style={{ marginBottom: "16px" }}
                      >
                        <h5
                          style={{
                            margin: "0 0 12px 0",
                            fontSize: "0.95rem",
                            color: "#1e293b",
                            fontWeight: 600,
                            borderBottom: "1px solid #e2e8f0",
                            paddingBottom: "8px",
                          }}
                        >
                          Role : {roleSkill.job_role_name}
                        </h5>
                        <div
                          className="skills-list"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                          }}
                        >
                          {roleSkill.skills &&
                            roleSkill.skills.map((skillObj, sIndex) => {
                              const skillName = Object.keys(skillObj)[0];
                              const skillData = skillObj[skillName];
                              return (
                                <div
                                  key={sIndex}
                                  className="card-item"
                                  style={{
                                    padding: "16px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "8px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        color: "#334155",
                                        fontSize: "0.9rem",
                                      }}
                                    >
                                      {skillName}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: "0.75rem",
                                        padding: "2px 8px",
                                        borderRadius: "12px",
                                        backgroundColor: "#f1f5f9",
                                        color: "#475569",
                                        fontWeight: 500,
                                        textTransform: "capitalize",
                                      }}
                                    >
                                      {skillData.level}
                                    </span>
                                  </div>

                                  {skillData.subSkills &&
                                    skillData.subSkills.length > 0 && (
                                      <div
                                        style={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: "6px",
                                        }}
                                      >
                                        {skillData.subSkills.map(
                                          (sub, subIndex) => (
                                            <span
                                              key={subIndex}
                                              style={{
                                                fontSize: "0.75rem",
                                                backgroundColor: "#e0f2fe",
                                                color: "#0369a1",
                                                padding: "2px 8px",
                                                borderRadius: "4px",
                                              }}
                                            >
                                              {typeof sub === "string"
                                                ? sub
                                                : `${sub.name}${sub.level ? ` (${sub.level})` : ""}`}
                                            </span>
                                          ),
                                        )}
                                      </div>
                                    )}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    ))}
                </div>

                <label className="peer-review-checkbox">
                  <input
                    type="checkbox"
                    {...register("peerReview")}
                    checked={
                      watch("peerReview") === 1 || watch("peerReview") === true
                    }
                    onChange={(e) => {
                      setValue("peerReview", e.target.checked ? 1 : 0);
                    }}
                  />
                  Hiring Manager Evaluated
                </label>

                {/* Fields moved from role to common */}
                <div className="work-details-section">
                  <h3>Work Details</h3>

                  <div className="work-details-field-group">
                    <label>Overall Experience *</label>
                  </div>
                  <div className="form-grid-2">
                    <div>
                      <input
                        type="number"
                        min="0"
                        placeholder="Enter value"
                        {...register("overallExperience.value", {
                          required: "Overall experience value is required",
                          valueAsNumber: true,
                        })}
                      />
                      <FormError
                        message={errors.overallExperience?.value?.message}
                      />
                    </div>
                    <div>
                      <select
                        {...register("overallExperience.unit", {
                          required: "Unit is required",
                          validate: (value) => {
                            const expValue = getValues(
                              "overallExperience.value",
                            );
                            if (expValue && !value) {
                              return "Unit is required";
                            }
                            return true;
                          },
                        })}
                      >
                        <option value="">Select unit</option>
                        <option value="years">Years</option>
                        <option value="months">Months</option>
                      </select>
                      {/* <ErrorMessage
                        errors={errors}
                        name="overallExperience.unit"
                        render={({ message }) => (
                          <p
                            style={{
                              color: "red",
                              fontSize: "14px",
                              marginTop: "5px",
                            }}
                          >
                            {message}
                          </p>
                        )}

                      /> */}
                      <FormError
                        message={errors.overallExperience?.unit?.message}
                      />
                    </div>
                  </div>

                  <div className="form-grid-2">
                    <div>
                      <label>Salary Expectation *</label>
                      <input
                        type="text"
                        placeholder="Enter amount"
                        value={formatSalaryInput(watch("salaryExpectation"))}
                        onChange={(e) => {
                          const rawVal = e.target.value.replace(/\D/g, "");
                          setValue("salaryExpectation", rawVal ? Number(rawVal) : "", { shouldValidate: true });
                        }}
                      />
                      <input
                        type="hidden"
                        {...register("salaryExpectation", {
                          required: "Salary expectation is required",
                        })}
                      />
                      <FormError message={errors.salaryExpectation?.message} />
                    </div>
                    <div>
                      <label>Salary Currency *</label>
                      <input
                        {...register("salaryCurrency", {
                          required: "Salary currency is required",
                        })}
                        placeholder="e.g., CHF, USD"
                      />
                      <FormError message={errors.salaryCurrency?.message} />
                    </div>
                  </div>

                  <div className="work-details-field-group">
                    <label>Salary Period *</label>
                    <select
                      {...register("salaryPeriod", {
                        required: "Salary period is required",
                      })}
                      className="half-width-select"
                    >
                      <option value="year">Year</option>
                      <option value="month">Month</option>
                      <option value="week">Week</option>
                      <option value="day">Day</option>
                    </select>
                    <FormError message={errors.salaryPeriod?.message} />
                  </div>
                </div>

                <div className="work-details-section">
                  <h3>Locations </h3>
                  <input
                    type="hidden"
                    {...register("location", {
                      validate: (val) => {
                        if (!val || val.length === 0) {
                          return "Please add at least one location.";
                        }

                        const invalid = val.some(
                          (loc) => !loc.country || !loc.cities || loc.cities.length === 0
                        );

                        if (invalid) {
                          return "Each location must have at least one city.";
                        }

                        return true;
                      }
                    })}
                  />
                  {/* Add new country + cities */}
                  <div className="work-details-field-group">
                    <label>Country *</label>
                    <Controller
                      control={control}
                      name="newLocation.country"
                      render={({ field }) => (
                        <div className="dropdown-container">
                          <input
                            type="text"
                            placeholder="Search country..."
                            value={countrySearch || field.value || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setCountrySearch(value);
                              if (value !== field.value) {
                                field.onChange("");
                                setValue("newLocation.countryCode", "");
                                setLocalCountries([]);
                                setCities([]);
                              }
                            }}
                            className="dropdown-search"
                            disabled={isCountryLocked}
                            style={{
                              backgroundColor: isCountryLocked
                                ? "#f1f5f9"
                                : "white",
                              cursor: isCountryLocked ? "not-allowed" : "text",
                            }}
                          />

                          {loadingCountries && <p>Loading countries...</p>}

                          {/* {!loadingCountries &&
                              countrySearch.trim().length > 0 &&
                              localCountries.length > 0 && (
                                <ul className="dropdown-list">
                                  {localCountries
                                    .filter(
                                      (country) =>
                                        !(
                                          Array.isArray(watch("location"))
                                            ? watch("location")
                                            : []
                                        ).some(
                                          (l) => l.country === country.name
                                        ) ||
                                        country.name ===
                                          watch("newLocation.country")
                                    )
                                    .map((country) => (
                                      <li
                                        key={country.code}
                                        onClick={() => {
                                          field.onChange(country.name);
                                          setValue(
                                            "newLocation.countryCode",
                                            country.code
                                          );
                                          setCountrySearch(country.name);
                                          setLocalCountries([]);
                                          setCitySearch("");
                                          setCities([]);
                                        }}
                                      >
                                        {country.name}
                                      </li>
                                    ))}
                                </ul>
                              )} */}

                          {!loadingCountries &&
                            countrySearch.trim().length > 0 &&
                            localCountries.length > 0 && (
                              <ul className="dropdown-list">
                                {localCountries
                                  .filter((country) => {
                                    const existingLocations = Array.isArray(
                                      watch("location"),
                                    )
                                      ? watch("location")
                                      : [];
                                    const editIndex = watch(
                                      "newLocation.editIndex",
                                    );

                                    // If we're editing, allow the country being edited
                                    if (
                                      editIndex !== null &&
                                      editIndex !== undefined
                                    ) {
                                      const editingCountry =
                                        existingLocations[editIndex]?.country;
                                      if (country.name === editingCountry) {
                                        return true;
                                      }
                                    }

                                    // Exclude already added countries
                                    return !existingLocations.some(
                                      (l) => l.country === country.name,
                                    );
                                  })
                                  .map((country) => (
                                    <li
                                      key={country.code}
                                      onClick={() => {
                                        field.onChange(country.name);
                                        setValue(
                                          "newLocation.countryCode",
                                          country.code,
                                        );
                                        setCountrySearch(country.name);
                                        setLocalCountries([]);
                                        setCitySearch("");
                                        setCities([]);
                                      }}
                                    >
                                      {country.name}
                                    </li>
                                  ))}
                              </ul>
                            )}
                        </div>
                      )}
                    />
                  </div>

                  <div className="work-details-field-group">
                    <label>Cities *</label>
                    <Controller
                      control={control}
                      name="newLocation.cityInput"
                      render={({ field }) => (
                        <div className="dropdown-container">
                          <input
                            type="text"
                            placeholder={
                              watch("newLocation.country")
                                ? "Search or type city name..."
                                : "Select a country first"
                            }
                            value={citySearch || field.value || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setCitySearch(value);
                              if (
                                value.trim().length > 0 &&
                                watch("newLocation.countryCode") &&
                                value !== field.value
                              ) {
                                fetchCities(
                                  watch("newLocation.countryCode"),
                                  value,
                                );
                              } else {
                                setCities([]);
                              }
                              if (value === "") field.onChange("");
                            }}
                            className="dropdown-search"
                          />

                          {loadingCities && <p>Loading cities...</p>}

                          {!loadingCities &&
                            citySearch.trim().length > 0 &&
                            cities.length > 0 && (
                              <ul className="dropdown-list">
                                {cities
                                  .filter(
                                    (city) =>
                                      !(
                                        watch("newLocation.cities") || []
                                      ).includes(city.name),
                                  )
                                  .map((city) => (
                                    <li
                                      key={city.name}
                                      onClick={() => {
                                        const currentCities =
                                          watch("newLocation.cities") || [];
                                        if (
                                          !currentCities.includes(city.name)
                                        ) {
                                          setValue("newLocation.cities", [
                                            ...currentCities,
                                            city.name,
                                          ]);
                                        }
                                        setCitySearch("");
                                        setCities([]);
                                      }}
                                    >
                                      {city.name}
                                    </li>
                                  ))}
                              </ul>
                            )}
                        </div>
                      )}
                    />
                  </div>
                  <FormError message={errors.location?.message} />
                  {/* Display selected cities for this country */}
                  {(watch("newLocation.cities") || []).length > 0 && (
                    <div className="chip-container">
                      {watch("newLocation.cities").map((city, i) => (
                        <div key={i} className="chip small">
                          <span className="chip-text">{city}</span>
                          <button
                            type="button"
                            className="chip-remove-btn"
                            onClick={() => {
                              const updated = watch(
                                "newLocation.cities",
                              ).filter((_, j) => j !== i);
                              setValue("newLocation.cities", updated);
                            }}
                          >
                            ✖
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="work-details-field-group">
                    <button
                      type="button"
                      className="add-btn"
                      onClick={() => {
                        const country = watch("newLocation.country");
                        const countryCode = watch("newLocation.countryCode");
                        const citiesArr = watch("newLocation.cities") || [];
                        const editIndex = watch("newLocation.editIndex");

                        if (!country) {
                          toast("⚠️ Please select a country first.", {
                            style: {
                              background: "#fffff",
                              // color: "#664d03",
                              // border: "1px solid #ffeeba",
                            },
                          });
                          return;
                        }

                        if (citiesArr.length === 0) {
                          toast("⚠️ Please add at least one city.", {
                            style: {
                              background: "#ffff",
                              // color: "#664d03",
                              // border: "1px solid #ffeeba",
                            },
                          });
                          return;
                        }

                        const rawLocations = watch("location");
                        const currentLocations = Array.isArray(rawLocations)
                          ? rawLocations
                          : [];

                        if (editIndex !== undefined && editIndex !== null) {
                          // Editing existing
                          const updated = [...currentLocations];
                          updated[editIndex] = {
                            country,
                            countryCode,
                            cities: citiesArr,
                          };
                          setValue("location", updated);
                        } else {
                          // Adding new
                          const existing = currentLocations.find(
                            (l) => l.country === country,
                          );
                          if (existing) {
                            const merged = currentLocations.map((l) =>
                              l.country === country
                                ? {
                                  ...l,
                                  cities: Array.from(
                                    new Set([...l.cities, ...citiesArr]),
                                  ),
                                }
                                : l,
                            );
                            setValue("location", merged, {
                              shouldValidate: true,
                            });
                          } else {
                            setValue(
                              "location",
                              [
                                ...currentLocations,
                                { country, countryCode, cities: citiesArr },
                              ],
                              { shouldValidate: true },
                            );
                          }
                        }

                        // Reset
                        setValue("newLocation", {
                          country: "Switzerland",
                          countryCode: "CH",
                          cityInput: "",
                          cities: [],
                          editIndex: null,
                        });
                        setCountrySearch("");
                        setCitySearch("");
                      }}
                    >
                      {watch("newLocation.editIndex") !== null &&
                        watch("newLocation.editIndex") !== undefined
                        ? "Update location"
                        : "Add location"}
                    </button>
                  </div>

                  {/* Display all locations with edit and delete */}
                  {(watch("location") || []).length > 0 && (
                    <div className="chip-container">
                      {watch("location").map((loc, index) => (
                        <div key={index} className="chip">
                          <span className="chip-text">
                            {loc.country}{" "}
                            {loc.cities?.length > 0 && (
                              <span style={{ fontSize: "13px", color: "#555" }}>
                                ({loc.cities.join(", ")})
                              </span>
                            )}
                          </span>
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <button
                              type="button"
                              className="chip-edit-btn"
                              onClick={() => {
                                setValue("newLocation", {
                                  country: loc.country,
                                  countryCode: loc.countryCode,
                                  cityInput: "",
                                  cities: loc.cities,
                                  editIndex: index,
                                });
                                setCountrySearch(loc.country);
                                setCitySearch("");
                                setLocalCountries([]);
                                setCities([]);
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                fill="currentColor"
                                viewBox="0 0 16 16"
                              >
                                <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="chip-remove-btn"
                              onClick={() => {
                                const updated = (watch("location") || []).filter((_, i) => i !== index);
                                setValue("location", updated, { shouldValidate: true });

                                // If no locations remain, reset only cities but keep Switzerland selected
                                if (updated.length === 0) {
                                  setValue("newLocation.cities", []);
                                  setCitySearch("");
                                }
                              }}
                            >
                              ✖
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="experience-section">
                  <h5>Relevant Experience </h5>
                  <input
                    type="hidden"
                    {...register("relevantExperience", {
                      validate: (val) =>
                        (val && val.length > 0) ||
                        "At least one experience entry is required",
                    })}
                  />
                  <div className="experience-item">
                    <div className="experience-row">
                      <div className="experience-field">
                        <label>Company or Industry *</label>
                        <input
                          type="text"
                          value={newExperience.company}
                          onChange={(e) =>
                            setNewExperience({
                              ...newExperience,
                              company: e.target.value,
                            })
                          }
                        />
                        {/* {experienceErrors.company && (
                          <p
                            style={{
                              color: "red",
                              fontSize: "14px",
                              marginTop: "5px",
                            }}
                          >
                            {experienceErrors.company}
                          </p>
                        )} */}
                        <FormError message={experienceErrors.company} />
                      </div>
                      <div className="experience-field">
                        <label>Role *</label>
                        <input
                          type="text"
                          value={newExperience.role}
                          onChange={(e) =>
                            setNewExperience({
                              ...newExperience,
                              role: e.target.value,
                            })
                          }
                        />
                        {/* {experienceErrors.role && (
                          <p
                            style={{
                              color: "red",
                              fontSize: "14px",
                              marginTop: "5px",
                            }}
                          >
                            {experienceErrors.role}
                          </p>
                        )} */}
                        <FormError message={experienceErrors.role} />
                      </div>
                    </div>
                    <div className="experience-row">
                      <div className="experience-field">
                        <label>Duration *</label>
                        <div className="duration-fields">
                          <div style={{ flex: 1 }}>
                            <input
                              type="number"
                              min="0"
                              placeholder="Enter value"
                              value={newExperience.durationValue}
                              onChange={(e) =>
                                setNewExperience({
                                  ...newExperience,
                                  durationValue: e.target.value,
                                })
                              }
                            />
                            <FormError
                              message={experienceErrors.durationValue}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <select
                              value={newExperience.durationUnit}
                              onChange={(e) =>
                                setNewExperience({
                                  ...newExperience,
                                  durationUnit: e.target.value,
                                })
                              }
                            >
                              <option value="">Select unit</option>
                              <option value="months">Months</option>
                              <option value="years">Years</option>
                            </select>
                            <FormError
                              message={experienceErrors.durationUnit}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                      <FormError message={errors.relevantExperience?.message} />
                    </div>
                    <button
                      type="button"
                      className="add-btn"
                      onClick={handleAddExperience}
                    >
                      Add
                    </button>
                  </div>
                </div>

                {watch("relevantExperience")?.length > 0 && (
                  <div className="chip-container">
                    {watch("relevantExperience").map((exp, index) => (
                      <div key={index} className="chip">
                        <span className="chip-text">
                          {exp.role}, {exp.company}
                          {exp.duration &&
                            exp.duration !== "N/A" &&
                            ` (${exp.duration})`}
                        </span>
                        <button
                          type="button"
                          className="chip-remove-btn"
                          onClick={() => handleRemoveExperience(index)}
                        >
                          ✖
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="system-section">
                  <h5>Additional Systems </h5>
                  <input
                    type="hidden"
                    {...register("additionalSystems", {
                      validate: (val) =>
                        (val && val.length > 0) ||
                        "At least one system is required",
                    })}
                  />
                  <div className="system-item">
                    <div className="system-row">
                      <div className="system-field">
                        <label>System *</label>
                        <input
                          type="text"
                          value={newSystem.system}
                          onChange={(e) =>
                            setNewSystem({
                              ...newSystem,
                              system: e.target.value,
                            })
                          }
                        />
                        {/* {systemErrors.system && (
                          <p
                            style={{
                              color: "red",
                              fontSize: "14px",
                              marginTop: "5px",
                            }}
                          >
                            {systemErrors.system}
                          </p>
                        )} */}
                        <FormError message={systemErrors.system} />
                      </div>
                      <div className="system-field">
                        <label>Level *</label>
                        <select
                          value={newSystem.level}
                          onChange={(e) =>
                            setNewSystem({
                              ...newSystem,
                              level: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Level</option>
                          {systemLevels.map((lvl) => (
                            <option key={lvl} value={lvl}>
                              {lvl}
                            </option>
                          ))}
                        </select>
                        {/* {systemErrors.level && (
                          <p
                            style={{
                              color: "red",
                              fontSize: "14px",
                              marginTop: "5px",
                            }}
                          >
                            {systemErrors.level}
                          </p>
                        )} */}
                        <FormError message={systemErrors.level} />
                      </div>
                    </div>
                    <FormError message={errors.additionalSystems?.message} />
                    <button
                      type="button"
                      className="add-btn"
                      onClick={handleAddSystem}
                    >
                      Add
                    </button>
                  </div>
                </div>

                {watch("additionalSystems")?.length > 0 && (
                  <div className="chip-container">
                    {watch("additionalSystems").map((sys, index) => (
                      <div key={index} className="chip">
                        <span className="chip-text">
                          {sys.system} ({sys.level})
                        </span>
                        <button
                          type="button"
                          className="chip-remove-btn"
                          onClick={() => handleRemoveSystem(index)}
                        >
                          ✖
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div
                className="modal-actions"
                style={{ position: "static", margin: 0, padding: "20px 0" }}
              >
                <button
                  type="button"
                  className="outline-btn"
                  onClick={handleCancel}
                  disabled={buttonLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="next-btn"
                  disabled={buttonLoading}
                >
                  {buttonLoading ? (
                    <>
                      <span className="button-spinner"></span>
                      Saving...
                    </>
                  ) : (
                    "Save & Submit"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Skills Editor Modal */}
      <SkillsEditor
        isOpen={isSkillsEditorOpen}
        onClose={() => setIsSkillsEditorOpen(false)}
        onSave={handleSaveSkills}
        rolesData={skillsEditorData}
      />
      <CandidateProfileEditDialog
        isOpen={isProfileEditOpen}
        onClose={() => setIsProfileEditOpen(false)}
        userId={selectedUserCareerJourneyId}
        onSaved={fetchPreFillData}
      />
    </>
  );
}

export default CareerJourneyFlow;
